'use client';

import { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { requestNotificationPermission, getFCMToken, saveFCMToken } from '@/lib/push-notifications';
import { useAuth } from '@/contexts/AuthContext';

export const NotificationPermissionPrompt = () => {
  const { user } = useAuth();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  useEffect(() => {
    // Check if we should show the prompt
    if (user && typeof window !== 'undefined' && 'Notification' in window) {
      // Don't show if already granted or denied
      if (Notification.permission === 'default') {
        // Check if user has dismissed the prompt before
        const dismissed = localStorage.getItem('notificationPromptDismissed');
        if (!dismissed) {
          // Show prompt after a short delay
          setTimeout(() => {
            setShowPrompt(true);
          }, 3000);
        }
      }
    }
  }, [user]);

  const handleEnableNotifications = async () => {
    setIsRequesting(true);
    try {
      const granted = await requestNotificationPermission();

      if (granted && user) {
        // Get FCM token and save it
        const token = await getFCMToken();
        if (token) {
          await saveFCMToken(user.uid, token);
        }
      }

      setShowPrompt(false);
    } catch (error) {
      // Error enabling notifications
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('notificationPromptDismissed', 'true');
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-[calc(1rem+env(safe-area-inset-right))] z-50 w-96 max-w-[calc(100vw-2rem)] animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="backdrop-blur-md bg-black/90 rounded-xl shadow-2xl border border-gray-700/50 p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-full bg-red-600/20 flex items-center justify-center">
              <Bell className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">Enable Notifications</h3>
              <p className="text-gray-400 text-xs">Stay updated on your shows</p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-500 hover:text-gray-300 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <p className="text-gray-300 text-sm mb-4">
          Get notified when new episodes are available, seasons premiere, or shows change status.
        </p>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={handleEnableNotifications}
            disabled={isRequesting}
            className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white font-medium text-sm py-2 px-4 rounded-lg transition-all"
          >
            {isRequesting ? 'Enabling...' : 'Enable'}
          </button>
          <button
            onClick={handleDismiss}
            className="px-4 py-2 text-gray-400 hover:text-white text-sm font-medium transition-colors"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};
